import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { LogOut, BarChart3, Users, Settings, Zap, Headset, SignalHigh } from 'lucide-react';

// Import tab components
import { AnalyticsTab } from '@/components/AdminTabs/AnalyticsTab';
import { AccountsTab } from '@/components/AdminTabs/AccountsTab';
import { SettingsTab } from '@/components/AdminTabs/SettingsTab';
import { IntegrationsTab } from '@/components/AdminTabs/IntegrationsTab';
import { AgentsTab } from '@/components/AdminTabs/AgentsTab';
import { MojoAirCommissionsTab } from '@/components/AdminTabs/MojoAirCommissionsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [merchants, setMerchants] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [errors, setErrors] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [filters, setFilters] = useState({
    province: '',
    transaction_type: 'all',
    start_date: '',
    end_date: ''
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('analytics');

  // Phase 0 — admin session token in HttpOnly cookie. Gate on profile.
  // Defensive parse: if localStorage was polluted with the literal string
  // "undefined" (from an earlier buggy login that wrote JSON.stringify(undefined)),
  // treat it as absent rather than crashing on JSON.parse.
  const token = localStorage.getItem('mojo_admin_token');
  const _rawAdminUser = localStorage.getItem('mojo_admin_user');
  let adminUser = null;
  try {
    adminUser = _rawAdminUser && _rawAdminUser !== 'undefined'
      ? JSON.parse(_rawAdminUser)
      : null;
  } catch (_) {
    adminUser = null;
    localStorage.removeItem('mojo_admin_user');
  }
  const isAdmin = !!adminUser;

  useEffect(() => {
    if (!isAdmin) {
      console.log('⚠️ No admin session found, redirecting to login');
      navigate('/admin/login');
      return;
    }

    // Only fetch analytics data when on analytics tab
    if (activeTab === 'analytics') {
      fetchAllAnalytics();
    } else {
      setLoading(false);
    }
  }, [activeTab]);

  const fetchAllAnalytics = async () => {
    setLoading(true);
    // Phase 0 — admin auth is now via the HttpOnly mojo_session cookie
    // (axios.defaults.withCredentials = true in src/lib/auth-client.js).
    // Only send a Bearer header if we actually have a token; never send
    // `Bearer null` because the backend will then reject the cookie path
    // and try to decode "null" as a JWT.
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      const [overviewRes, merchantsRes, customersRes, errorsRes, integrationsRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview`, { headers }).catch(e => ({ data: null })),
        axios.get(`${API}/admin/analytics/merchants`, { headers }).catch(e => ({ data: null })),
        axios.get(`${API}/admin/analytics/customers`, { headers }).catch(e => ({ data: null })),
        axios.get(`${API}/admin/analytics/errors`, { headers }).catch(e => ({ data: null })),
        axios.get(`${API}/admin/analytics/integrations`, { headers }).catch(e => ({ data: null })),
      ]);

      setOverview(overviewRes.data);
      setMerchants(merchantsRes.data);
      setCustomers(customersRes.data);
      setErrors(errorsRes.data);
      setIntegrations(integrationsRes.data);
    } catch (error) {
      console.error('❌ Analytics fetch failed:', error);
      toast.error('Failed to fetch analytics');
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params = new URLSearchParams();

    if (filters.transaction_type && filters.transaction_type !== 'all') {
      params.append('transaction_type', filters.transaction_type);
    }
    if (filters.province) params.append('location', filters.province);
    if (filters.start_date) params.append('start_date', new Date(filters.start_date).toISOString());
    if (filters.end_date) params.append('end_date', new Date(filters.end_date).toISOString());

    try {
      const response = await axios.get(`${API}/admin/analytics/breakdown?${params}`, { headers });
      setBreakdown(response.data);
      toast.success('Filters applied');
    } catch (error) {
      toast.error('Failed to apply filters');
    }
  };

  const handleLogout = async () => {
    // Phase 0 — clear cookie server-side + scrub remaining localStorage.
    try { await axios.post(`${API}/auth/logout`); } catch (_) { /* offline ok */ }
    localStorage.removeItem('mojo_admin_token');
    localStorage.removeItem('mojo_admin_user');
    toast.success('Logged out');
    navigate('/admin/login');
  };

  if (loading && activeTab === 'analytics') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#F2B10C] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-[#1B0C34]">Loading Analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5F0] via-[#F5EDE4] to-[#F5E8D8]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#F2B10C]/20 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/sticker.svg" alt="Mojo Money" className="h-10 w-10" />
            <div>
              <h1 className="text-xl font-bold text-[#1B0C34]">Mojo Money Admin</h1>
              <p className="text-xs text-[#145539]">System Management</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => navigate('/admin/kyc')}
              variant="outline"
              size="sm"
              className="border-[#F2B10C]"
              data-testid="admin-nav-kyc"
            >
              KYC Review
            </Button>
            <Button onClick={handleLogout} variant="outline" size="sm" className="border-[#F2B10C]">
              <LogOut size={16} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 sm:grid-cols-6 mb-6">
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 size={16} />
              <span className="hidden sm:inline">Analytics</span>
            </TabsTrigger>
            <TabsTrigger value="accounts" className="flex items-center gap-2">
              <Users size={16} />
              <span className="hidden sm:inline">Accounts</span>
            </TabsTrigger>
            <TabsTrigger value="agents" className="flex items-center gap-2">
              <Headset size={16} />
              <span className="hidden sm:inline">Agents</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings size={16} />
              <span className="hidden sm:inline">Settings</span>
            </TabsTrigger>
            <TabsTrigger value="integrations" className="flex items-center gap-2">
              <Zap size={16} />
              <span className="hidden sm:inline">Integrations</span>
            </TabsTrigger>
            <TabsTrigger value="mojoair" className="flex items-center gap-2">
              <SignalHigh size={16} />
              <span className="hidden sm:inline">Mojo Air</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analytics" className="space-y-6">
            <AnalyticsTab
              overview={overview}
              merchants={merchants}
              customers={customers}
              errors={errors}
              integrations={integrations}
              breakdown={breakdown}
              filters={filters}
              setFilters={setFilters}
              applyFilters={applyFilters}
              loading={loading}
            />
          </TabsContent>

          <TabsContent value="accounts">
            <AccountsTab token={token} />
          </TabsContent>

          <TabsContent value="agents">
            <AgentsTab token={token} />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsTab token={token} />
          </TabsContent>

          <TabsContent value="integrations">
            <IntegrationsTab token={token} />
          </TabsContent>

          <TabsContent value="mojoair">
            <MojoAirCommissionsTab token={token} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
